import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  
  PageRoute,
  pn,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import {
  UserFilter as UserFilterModel,
  UserListDto,
  UserListResource,
  Permission,
  PermissionAction,
  UserListFilter,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { userPageRoute } from "../UserPage/UserPage";
import { UserFilter } from "../UserFilter";
import { UserTable } from "./UserTable";
import { ListFooter } from "components";
import { useDispatch } from "react-redux";

const routeName = "users";

export const userListPageRoute: PageRoute = {
  handle: "Users",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

const ROOT_PERMISSION: Permission = Permission.User;

export const UserListPage = () => {
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<UserListFilter>({
    sortObjects: [{ property: pn<UserListDto>("roleDisplayOrder"), isDesc: false }],
    userFilter: {
      isActive: true
    }
  });

  const [initListFilter] = useState<UserListFilter>(listFilter);
  const [listItems, setListItems] = useState<UserListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<UserListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, loadList, applyPage, applyPageSize, applySortMethod, applySort, applySearch } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.users.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.users.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.users.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<UserListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <UserFilter
        filter={listFilter.userFilter || ({} as UserFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.userFilter = filter;
            })
          )
        }
      />
    ),
  });

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{userListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button onClick={() => navigate(userPageRoute.getPath({ membership: "0", crudMode: "edit" }))} variant="outlined">
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <UserTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              users={listItems}
              resource={resource}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
